import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import Constants from 'src/core/utils/Constants';
import Dimens from 'src/core/utils/Dimens';

type Focusable = {
	focus: () => void;
};

const useWebAutoFocus = <T extends Focusable>(isActive: boolean) => {
	const ref = useRef<T | null>(null);

	useEffect(() => {
		if (Platform.OS !== Constants.platformWeb || !isActive) {
			return undefined;
		}

		let timer: ReturnType<typeof setTimeout> | undefined;

		const focusWhenAllowed = (afterReturningToTheApp: boolean) => {
			clearTimeout(timer);

			timer = setTimeout(() => {
				const isIdle = document.activeElement === null || document.activeElement === document.body;

				if (!document.hasFocus() || (afterReturningToTheApp && !isIdle)) {
					return;
				}

				ref.current?.focus();
			}, Dimens.animationDuration300);
		};

		const focusOnReturn = () => focusWhenAllowed(true);

		focusWhenAllowed(false);
		window.addEventListener(Constants.domEventFocus, focusOnReturn);

		return () => {
			clearTimeout(timer);
			window.removeEventListener(Constants.domEventFocus, focusOnReturn);
		};
	}, [isActive]);

	return ref;
};

export default useWebAutoFocus;
